import * as React from 'react';
import * as moment from 'moment';
import Toggle from "react-toggle";
import { config } from '@src/config';
import { vars, css, formatters, classNames, defaultProps } from '@src/services/theme';
import {api, bindToApp, confirm} from '@src/services/api';
import { _ } from "@src/services/i18n";
import Loading from '@src/components/UI/Loading';
import { Modal } from '@src/components';

export interface Props {
    noInitialLoad?: boolean;
    cpt?: string;
    get?: string;
    onBattery?: Function;
}
export interface State {
    data: any;
    startDate: moment.Moment;
    endDate: moment.Moment;
    focusedInput: "startDate" | "endDate" | null;
}

export class Proto<T extends Props, S extends State> extends React.Component<T, S> {

    state = {
    } as any;
    
    async componentWillMount() {
        const { cpt, noInitialLoad = false } = this.props;
        if (cpt && !noInitialLoad) {
            const res: any = await this.loadCptData(cpt, { memoization: true } );
            if (res.status === 200) {
                this.setState({
                    data: res.data,
                });
            } else {
            }
        }
    }

    DatePickerProps = () => ({
        startDatePlaceholderText: _('datepicker_start'),
        endDatePlaceholderText: _('datepicker_end'),
        isOutsideRange: day => (moment().diff(day) < 0),
        startDate: this.state.startDate,
        startDateId: "filter_start",
        endDate: this.state.endDate,
        endDateId: "filter_end",
        onDatesChange: ({ startDate, endDate }) => this.setState({ startDate, endDate }),
        focusedInput: this.state.focusedInput,
        onFocusChange: focusedInput => this.setState({ focusedInput }),
        keepOpenOnDateSelect: true,
        reopenPickerOnClearDates: true,
        onClose: ({ startDate, endDate }) => { if (this['loadWithFilters']) this['loadWithFilters']({ startDate, endDate }) }
    });

    TempGraphProps = () => ({
        type: "stock",
            theme: "light",
            dataSets: [{
                "color": vars.colors.greyDark,
                fieldMappings: [ {
                    "fromField": "value",
                    "toField": "value"
                }],
                dataProvider: this.parseTempGraphData(),
                categoryField: "date",
                stockEvents: this.parseTempGraphEvents()
            }],
            "panels": [ {
                "stockGraphs": [ {
                  "id": "g1",
                  "valueField": "value",
                  //"type": "smoothedLine",
                  "lineThickness": 2,
                  "bullet": "round"
                } ],
                "stockLegend": {
                  "valueTextRegular": " ",
                  "markerType": "none"
                }
            } ],
            chartScrollbarSettings: {
                enabled: false
                //"graph": "g1"
            },
            "chartCursorSettings": {
                "valueBalloonsEnabled": true,
                "graphBulletSize": 1,
                "valueLineBalloonEnabled": true,
                "valueLineEnabled": true,
                "valueLineAlpha": 0.5
            },
            "categoryAxesSettings": {
                "minPeriod": "mm"
            },
            "zoomOutOnDataSetChange": true,
            "preloader": {
                "disabled": false
            }
    });

    loadCptData( cpt, args = {} as any ) {
        let { get } = this.props;
        if (args.url) {
            get = args.url;
        }
        const params = {...this.transformFilters(args)};
        return new Promise( async (resolve, reject) => {
            try {
                const res = await api({
                    url: (get || config.api.mock_url),
                    method: 'get',
                    params: {
                        cpt,
                        ...params
                    },
                    memoization: args.memoization
                });
                resolve( res );
            } catch(e) {
                reject(e);
            }
        });
    }

    transformFilters( filters ) {
        // manage params transformations
        const format = 'YYYY-MM-DD';
        let params = {...filters};
        if (filters.startDate) {
            params.from = filters.startDate.format( format );
            delete params.startDate;
        }
        if (filters.endDate) {
            params.to = filters.endDate.format( format );
            delete params.endDate;
        }
        return params;
    }

    async loadWithFilters( filters ) {
        let state = { ...filters };
        if (filters.startDate && filters.endDate) {
            const res: any = await this.loadCptData( this.props.cpt, filters );
            state.data = res.data;
        }
        this.setState({
            ...state,
            loading: false
        });
        if (state.data) {
            return state.data;
        }
    }

    async loadWhenFiltersChanged( props, oldProps ) {
        let reload = false;
        if (!props.filters || !oldProps.filters) {
            reload = true;
        }
        if (props.filters && oldProps.filters) {
            if (props.filters.time != oldProps.filters.time) {
                reload = true;
            }
        }
        if (reload) {
            const res: any = await this.loadCptData( this.props.cpt, props.filters );
            this.setState({
                data: res.data,
            });
            return res.data;
        }
        return null;
    }

    // temperature graph helpers
    parseTempGraphData() {
        const { data } = this.state;
        var els = data.data.temperatures.data.map( v => {
            const { date, temperature } = v;
            return {
                date: moment(date).toDate(),
                value: temperature
            }
        });

        data.data.events.forEach( v => {
            const date = moment(v.date);
            for (let i=0; i<els.length; i++) {
                let d = els[i];
                if (date.isBefore(d.date)) {
                    // adding event datetime (yeah, it's fake)
                    els.splice(i, 0, {
                        date: moment(d.date).toDate(),
                        value: d.value,
                        cloned: true
                    });
                    return;
                }
            }
        });

        return els;
    }

    parseTempGraphEvents() {
        const { data } = this.state;
        return data.data.events.map( (v,i) => {
            const { date, soc } = v;
            return {
                date: moment(date).toDate(),
                graph: "g1",
                type: "sign",
                "text": `${i+1}`,
                "backgroundColor": vars.colors.yellow,
                showAt: "high",
                showBullet: true,
                description: `<strong>${soc}</strong> SOC\n${formatters.datetime(date)}`
            }
        })
    }

    addDefaultOption( arr, label = _('default_option') ) {
        return [{value: null, label}].concat( arr );
    }

    // ui
    loading() {
        if (!this.props.cpt) {
            return false;
        }
        if (!this.state || !this.state.data) {
            return (
                <Loading />
            );
        }
        return false;
    }
}

export interface PropsList extends Props {
    urlIndex?: string;
    urlShow?: string;
    urlCreate?: string;
    urlUpdate?: string;
    urlDelete?: string;
    fields?: any;
}
export interface StateList extends State {
    filters?: any;
    items?: any[];
    meta?: {
        current_page: number,
        from: number,
        last_page: number,
        path: string,
        per_page: number,
        to: number,
        total: number
    },
    tableProps?: any;
    // editing
    action: "CREATE" | "EDIT" | null,
    editing?: any;
    editProps?: any;
    loading?: boolean;
};

export class ProtoList<T extends PropsList, S extends StateList> extends Proto<T, S> {
    state = {
        tableProps: {
            manual: true,
            onFetchData: (args) => this.fetchData(args),
            showPagination: true,
            minRows: 15,
            getTrProps: (state, row, column) => {
                const { fields } = this.props;
                if (!row) return {};
                return {
                    className: classNames("clickable", fields && fields.status && row.original[fields.status] === false ? 'disabled' : null),
                    onClick: e => {
                        this.edit(row.original);
                    },
                };
            }
        }
    } as any;

    Toolbar = () => (
        <div>
            <a className="btn btn-success" onClick={() => this.create()}><i className="fa fa-plus" /> Add</a>
        </div>
    );

    SwitchStatus = row => {
        const { fields } = this.props;
        const id = row.original[fields.id];
        const status = row.original[fields.status];
        return (
            <div onClick={(e) => e.stopPropagation()}>
                <Toggle
                // checked={value}
                // name={field}
                id={`${id}`}
                checked={status}
                onChange={(e) => {
                    const v = e.target.checked;
                    this.doSwitchStatus(row.row);
                } }
                />
            </div>
        );
    };

    SwitchColumn = {
       ...defaultProps.columns.switch, Cell: this.SwitchStatus
    };

    async componentWillMount() {
        const { cpt, noInitialLoad, get, urlShow, urlCreate, urlUpdate } = this.props;
        if (cpt && !noInitialLoad) {
            this.setState({
                loading: true
            });
            const res: any = await this.loadCptData(cpt);
            if (res.status === 200) {
                this.setState({
                    data: res.data,
                    items: res.data.data || [],
                    loading: false,
                });
            } else {
            }
        }
        this.setState({
            editProps: {
                cpt,
                get,
                urlShow, urlCreate, urlUpdate,
                onSuccess: () => this.saved(),
                onCancel: () => this.cancel(),
            }
        })
    }

    // override
    async loadWithFilters( filters ) {
        this.setState({loading: true});
        const data = await super.loadWithFilters(filters);

        // Non aggiornare la table se i dati non sono corretti
        if (data == undefined) return;

        this.setState({
            tableProps: {
                ...this.state.tableProps,
                pages: data.meta ? data.meta.last_page : 1
            },
            items: data.data,
            meta: data.meta,
            filters: {
                ...filters,
                page: 1
            },
            loading: false
        });
    }

    async loadWhenFiltersChanged( props, oldProps ) {
        this.setState({loading: true});
        const data = await super.loadWhenFiltersChanged( props, oldProps );
        this.setState({
            tableProps: {
                ...this.state.tableProps,
                pages: data.meta ? data.meta.last_page : 1
            },
            items: data.data,
            meta: data.meta,
            filters: {
                ...this.state.filters,
                ...props.filters,
                page: 1
            },
            loading: false
        });
    }

    //
    create() {
        this.setState({
            action: "CREATE"
        });
    }

    edit( item ) {
        const { fields } = this.props;
        this.setState({
            action: "EDIT",
            editing: item[fields.id]
        });
    }

    cancel() {
        this.setState({
            action: null,
            editing: null
        });
    }

    saved() {
        this.setState({
            action: null,
            editing: null
        });
        this.reload();
    }

    reload() {
        const args = {
            page: this.state.filters.page-1,
        }
        this.fetchData( args );
    }

    async search( filters ) {
        const { urlIndex } = this.props;
        this.setState({loading: true});
        try {
            const res = await api({
                url: urlIndex,
                method: 'get',
                params: this.transformFilters(filters)
            });
            const { data, meta } = res.data;
            this.setState({
                items: data,
                filters,
                meta,
                loading: false,
                tableProps: {
                    ...this.state.tableProps,
                    pages: meta ? meta.last_page : null
                }
            });
        } catch(e) {}
    }

    fetchData( tableState ) {
        const { filters = {} } = this.state;
        let sort = {};
        if (tableState.sorted && tableState.sorted.length) {
            const _sort = tableState.sorted[0];
            sort = { sortBy: _sort.id, sortDir: _sort.desc ? 'desc' : 'asc' };
        }
        this.search( {
            ...filters,
            ...sort,
            page: tableState.page+1 // fix zero-based
        } );
    }

    async doSwitchStatus( row ) {
        const { urlUpdate = "", fields } = this.props;
        const { id, status } = fields as any;
        const url = urlUpdate;
        try {
            const res = await api({
                url: FixUrl(url, row[id]),
                method: "PATCH",
                data: {
                    [status]: !row[status]
                }
            });
            this.reload();
        } catch(e) {}
    }
}

export interface PropsEdit extends Props {
    id?: any;
    urlShow?: string;
    urlCreate?: string;
    urlUpdate?: string;
    urlDelete?: string;

    onSuccess?: Function;
    onError?: Function;
    onCancel?: Function;
}
export interface StateEdit extends State {
    values?: any;
    entity?: any;
    error?: any;
    busy?: boolean;
}

export function FixUrl( url, id ) {
    return url.replace("%5Bid%5D", `${id}`); // [id] 
}

export class ProtoEdit<T extends PropsEdit, S extends StateEdit> extends Proto<T, S> {
    state = {
    } as any;

    constructor(props){
        super(props);
        this.setState({
            busy: false
        });

    }

    private fixUrl( url, id ) {
        return FixUrl( url, id );
    }

    async componentWillMount() {
        super.componentWillMount();
        await this.loadEdit();
    }

    async loadEdit() {
        const { id, urlShow = "", onCancel } = this.props;
        if (id) {
            // EDIT
            try {
                const res = await api({
                    url: this.fixUrl(urlShow, id),
                    method: 'get'
                });
                const { data } = res.data;
                this.setState({
                    entity: data
                }, () => {
                    this.postLoad( data )
                });
            } catch(e) {
                if (onCancel) onCancel()
            }
        } else {
            // CREATE
            this.setState({
                entity: {}
            });
        }
    }

    postLoad( entity ) {}

    async save(v, toBeConfirmed: any = false) {

        this.setState({
            busy: true
        });

        if (toBeConfirmed) {
            try {
                await confirm(toBeConfirmed);
            } catch(e) {
                return;
            }
        }
        const { id, urlCreate = "", urlUpdate = "", onSuccess, onError } = this.props;
        const url = id ? urlUpdate : urlCreate;
        const method = id ? "PATCH" : "POST";
        try {
            const res = await api({
                url: this.fixUrl(url, id),
                method,
                data: v
            });
            if (onSuccess) {
                onSuccess(res);
            }
        } catch(e) {
            this.setState({
                error: e.response.data,
                busy: false
            })
        }
    }

    outputError() {
        const { error } = this.state;
        if (!error.errors) { return null; }
        const errors = Object.keys(error.errors).reduce( (acc, e) => {
            return [...acc, ...error.errors[e] ];
        }, [])
        return (
            <div className="errors">
                <div><strong>{error.message}</strong></div>
                <ul>
                    {errors.map( (v,k) => (
                        <li key={k}>{v}</li>
                    ) )}
                </ul>
            </div>
        )
    }
}


// WEBPACK FOOTER //
// ./src/components/Proto.tsx